// eslint-disable-next-line no-unused-vars
import React, { forwardRef } from 'react';
import { func, oneOfType, number, bool, object, string } from 'prop-types';
import { Button } from '@carbon/react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { FileText, Heart, ShoppingCart, X } from 'lucide-react';

import OffersMenu from 'Containers/Offers/OffersMenu';
import WishlistsMenu from 'Containers/Wishlists/WishlistsMenu';
import CartMenuDropdown from 'Containers/Cart/CartMenuDropdown';
import { StyledOverlay as Overlay } from 'Components/Products/Products';
import useScreen from 'Hooks/useScreen';
import useUser from 'Hooks/useUser';
import usePermissions from 'Hooks/usePermissions';
import { PERMISSION } from 'Constants';

const StyledOverlay = styled(Overlay)`
  display: ${ props => props.open ? 'block' : 'none' };
  width: 100vw;
  height: 100vh;
  min-height: 102vh;
  left: 0;
  top: ${ props => props.theme.canFloat ? '50px' : 0 };
  position: fixed;
  background-color: rgba(0, 0, 0, 0.7);
`;

const StyledContainer = styled.div`
  margin: 0 -10px;
  position: relative;
  z-index: 12;
  display: flex;
  align-content: center;
  justify-content: space-between;

  ${ props => !props.isOpen && `> button { color: ${ props.theme.colors.white } }` };

  @media screen and (max-width: ${ props => props.theme.device.tablet }) {
    margin-left: 0;
  }
`;

const StyledMenuButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 9px;
  padding: 0 10px;
  outline: 0;
  border: 0;
  box-shadow: none !important;
  background-color: transparent !important;
  position: relative;
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.25);

  &.active {
    color: ${ props => props.theme.colors.white };
  }

  &.lists svg,
  &.offers svg {
    width: 26px;
  }

  &.cart svg {
    width: 32px;
  }

  &.lists,
  &.offers,
  &.cart {
    svg {
      margin-right: 10px;
    }
  }

  .cart-items {
    position: absolute;
    top: -5px;
    left: 15px;
    width: 20px;
    height: 20px;
    border-radius: 20px;
    text-align: center;
    line-height: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    background-color: #D52B1E;
    color: ${ props => props.theme.colors.white };
  }

  @media screen and (max-width: ${ props => props.theme.device.tablet }) {
    margin: 0 6px;

    &.lists,
    &.offers {
      width: 26px;
    }

    &.cart {
      width: 32px;
    }

    &.lists,
    &.offers,
    &.cart {
      svg {
        margin-right: 6px;
      }
    }

    span:not(.cart-items) {
      display: none;
    }
  }
`;

const DropdownMenu = styled.div`
  position: ${ props => props.canFloat ? 'fixed' : 'absolute' };
  right: ${ props => props.canFloat ? 0 : '-45px' };
  top: ${ props => props.canFloat ? '118px' : '62px' };
  padding: 0;
  width: 650px;
  min-width: 650px;
  max-width: 100%;
  border: 1px solid ${ props => props.theme.products.colors.border };
  box-sizing: border-box;
  // max-height: ${ props => props.canFloat ? 'calc(100vh - 70px)' : 'calc(100vh - 143px)' };
  overflow: visible;
  ${ props => !props.isOpen && `
    opacity: 0;
    visibility: hidden;
    width: 0;
  ` }
  color: ${ props => props.theme.colors.darkBlue };
  background-color: ${ props => props.theme.colors.white };
  z-index: 9;
  transform: ${ props => props.isOpen ? 'translateX(0)' : 'translateX(101%)' };
  transition: transform ${ props => props.isOpen ? '0.35s' : '0.15s' } ease-out;

  .tabs {
    min-height: 0;

    ul {
      width: 100%;
      background-color: transparent;
      box-shadow: none;
      outline: 0;
      border: 0;
      display: flex;
      justify-content: space-between;
      align-content: center;

      li {
        &:last-of-type {
          width: 40px;
          flex-grow: 0;
          border-left: 1px solid ${ props => props.theme.colors.white };

          button {
            background-color: #041B54;

            svg {
              width: auto;
              margin: 0 10px;
            }
          }
        }

        &.selected {
          button {
            color: #1D4578 !important;
            background-color: ${ props => props.theme.colors.white };
            outline: 0;
            border: 0;
          }
        }

        flex-grow: 1;
        max-height: 40px;
        box-shadow: none !important;

        button {
          &.close {
            color: ${ props => props.theme.colors.white } !important;
            svg {
              transform: none;
            }
          }
          color: ${ props => props.theme.colors.gray } !important;
          font-size: 1rem !important;
          padding: 0 !important;
          background-color: ${ props => props.theme.products.colors.border };
          height: 40px !important;
          text-align: center !important;
          display: flex;
          justify-content: center;
          width: 100% !important;
          align-items: center;
          box-shadow: none !important;
          outline: 0;
          border: 0;
          cursor: pointer;

          svg {
            width: 27px;
            margin-right: 5px;
            transform: scale(0.75);
          }
        }
      }
    }
  }

  .cds--tab-content {
    padding: 2px 0 0;

    .productList {
      z-index: 1;
      overflow: auto;
      max-height: ${ props => props.canFloat ? 'calc(100vh - 350px)' : 'calc(100vh - 450px)' };
    }

    .productList + footer {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: space-between;
      box-shadow: 0 0 8px #00000029;
      padding: 8.5px 0;
      z-index: 0;

      button {
        font-size: 1em;
        line-height: 1em;
        margin: 7.5px auto;
        width: 40%;
        flex-grow: 1;
        max-width: 255px;
        text-align: center;
        display: flex;
        padding: 0;
        height: 40px;
        justify-content: center;

        &:first-of-type {
          color: ${ props => props.theme.colors.white };
        }

        &:last-of-type {
          border: 1px solid ${ props => props.theme.colors.blue };
          color: ${ props => props.theme.colors.blue };
        }
      }
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    right: ${ props => props.canFloat ? 0 : '-32px' };
  }

  @media screen and (max-width: ${ props => props.theme.device.tablet }) {
    right: ${ props => props.canFloat ? 0 : '-16px' };
  }
`;

const CartAndWishlists = forwardRef((props, ref) => {
  const { open, activeTab, onClose, onSetActive, cart, onToggle, canFloat } = props;
  const theme = useTheme();
  const { t } = useTranslation();
  const screen = useScreen();
  const { data: { isImpersonated } = {} } = useUser();
  const { can } = usePermissions();
  const canAccessOffers = isImpersonated === false
    && can(PERMISSION.ACCESS_ACCOUNT_FEATURES);

  return (
    <div>
      <StyledOverlay
        open={ open }
        theme={{ canFloat }}
      />
      <StyledContainer ref={ ref } isOpen={ open } onKeyDown={ onToggle }>
        { canAccessOffers && (
          <StyledMenuButton
            theme={ theme }
            onClick={ onToggle(0) }
            className={ `offers ${ (activeTab === 0 && open && 'active') }` }
            tabIndex={ 0 }
          >
            <FileText size={ 28 } />
            <span>{ t('offers.offers') }</span>
          </StyledMenuButton>
        ) }
        <StyledMenuButton
          theme={ theme }
          onClick={ onToggle(1) }
          className={ `lists ${ (activeTab === 1 && open && 'active') }` }
          tabIndex={ 0 }
        >
          <Heart size={ 28 } />
          <span>{ t('navigation.lists') }</span>
        </StyledMenuButton>
        <StyledMenuButton
          theme={ theme }
          onClick={ onToggle(2) }
          className={ `cart ${ (activeTab === 2 && open && 'active') }` }
          tabIndex={ 0 }
        >
          { cart?.totalItems > 0 && (
            <span className="cart-items">{ cart?.totalItems || '' }</span>
          ) }
          <ShoppingCart size={ 28 } className="cart-icon" />
          <span>{ t('navigation.cart') }</span>
        </StyledMenuButton>
        { screen.width > 767 && (
          <DropdownMenu theme={ theme } isOpen={ open } canFloat={ canFloat }>
            <div
              className="tabs cds--tabs--scrollable cds--tabs--scrollable--container cds--tabs--scrollable--light"
            >
              <ul role="tablist" tabIndex={ -1 } className="cds--tabs--scrollable-nav">
                { canAccessOffers && (
                  <li
                    role="presentation"
                    className={ `cds--tabs--scrollable__nav-item ${ activeTab === 0 && 'selected' }` }
                  >
                    <button
                      type="button"
                      role="tab"
                      aria-selected="false"
                      aria-controls="offers__panel"
                      id="offers"
                      className="cds--tabs--scrollable__nav-link"
                      tabIndex="-1"
                      data-test="onSetActiveTab"
                      data-testid="offers"
                      onClick={ () => onSetActive(0) }
                    >
                      <FileText />
                      { t('offers.offers') }
                    </button>
                  </li>
                ) }
                <li
                  role="presentation"
                  className={ `cds--tabs--scrollable__nav-item ${ activeTab === 1 && 'selected' }` }
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected="false"
                    aria-controls="wishlists__panel"
                    id="wishlists"
                    className="cds--tabs--scrollable__nav-link"
                    tabIndex="-1"
                    data-test="onSetActiveTab"
                    data-testid="wishlists"
                    onClick={ () => onSetActive(1) }
                  >
                    <Heart />
                    { t('navigation.wishlists') }
                  </button>
                </li>
                <li
                  role="presentation"
                  className={ `cds--tabs--scrollable__nav-item ${ activeTab === 2 && 'selected' }` }
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected="false"
                    aria-controls="cart__panel"
                    id="cart"
                    className="cds--tabs--scrollable__nav-link"
                    tabIndex="-1"
                    data-test="onSetActiveTab"
                    data-testid="cart"
                    onClick={ () => onSetActive(2) }
                  >
                    <ShoppingCart />
                    { t('navigation.cart') }
                  </button>
                </li>
                <li role="presentation" className="cds--tabs--scrollable__nav-item">
                  <button
                    type="button"
                    role="tab"
                    aria-selected="false"
                    aria-controls="wishlists__panel"
                    id="wishlists"
                    className="cds--tabs--scrollable__nav-link close"
                    tabIndex="-1"
                    onClick={ onClose }
                  >
                    <X />
                  </button>
                </li>
              </ul>
            </div>
            { canAccessOffers && (
              <div
                role="tabpanel"
                hidden={ activeTab !== 0 }
                className="cds--tab-content"
                aria-labelledby="offers"
              >
                <OffersMenu active={ activeTab === 0 } open={ open } />
              </div>
            ) }
            <div
              role="tabpanel"
              hidden={ activeTab !== 1 }
              className="cds--tab-content"
              aria-labelledby="wishlists"
            >
              <WishlistsMenu active={ activeTab === 1 } open={ open } />
            </div>
            <div
              role="tabpanel"
              hidden={ activeTab !== 2 }
              className="cds--tab-content"
              aria-labelledby="cart"
            >
              <CartMenuDropdown />
            </div>
          </DropdownMenu>
        )}
      </StyledContainer>
    </div>
  );
});

CartAndWishlists.propTypes = {
  open: bool,
  onClose: func,
  activeTab: oneOfType([string, number]),
  cart: object,
  canFloat: bool,
  onSetActive: func,
  onToggle: func,
};
CartAndWishlists.displayName = 'CartAndWishlists';

export default CartAndWishlists;
